'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { usePathname } from 'next/navigation';
import { useEffect, useState, type ReactNode } from 'react';

import type { ContactLink, HeaderData } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2';

type MobileMenuProps = Pick<HeaderData, 'topbarLinks' | 'phone' | 'email'> & {
  logo: ReactNode;
};

/**
 * Menu below `lg` (Figma „Open menu“ 60460:121182): a 342 px sheet from the left over the dimmed
 * page – logo and close, the top bar menu centred, „Potrebujete poradiť?“ with the phone
 * and e-mail. The catalogue lives behind „Kategórie“ in the mobile navbar.
 */
export function MobileMenu({ topbarLinks, phone, email, logo }: MobileMenuProps) {
  const { t } = useTranslations();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label={t('text3241')}
        className={`flex size-11 shrink-0 items-center justify-center rounded-full bg-[#F5F6F7] text-[#0F1012] lg:hidden ${FOCUS}`}
      >
        <Icon name="menu" size={24} />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[#0F1012]/30" />
        <Dialog.Content
          aria-describedby={undefined}
          onClick={event => {
            // A link to the current page does not change the pathname.
            if ((event.target as HTMLElement).closest('a')) setOpen(false);
          }}
          className="fixed inset-y-0 left-0 z-50 flex w-[342px] max-w-[calc(100%-48px)] flex-col gap-6 overflow-y-auto rounded-r-[18px] bg-white px-4 py-6 focus:outline-none"
        >
          <Dialog.Title className="sr-only">{t('text3241')}</Dialog.Title>
          <div className="flex items-center justify-between">
            {logo}
            <Dialog.Close
              aria-label={t('text272')}
              className={`-m-2.5 flex size-11 items-center justify-center rounded-full text-[#6B7079] ${FOCUS}`}
            >
              <Icon name="x" size={24} />
            </Dialog.Close>
          </div>
          {topbarLinks.length ? (
            <nav aria-label={t('text3241')}>
              <ul className="flex flex-col">
                {topbarLinks.map(link => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      prefetch={false}
                      className={`dlz-h5 flex justify-center py-3 text-[18px] text-[#0F1012] transition-colors hover:text-[#D7191F] ${FOCUS}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
          {phone || email ? (
            <div className="flex flex-col gap-4">
              <p className="dlz-body-s text-[#4F545C]">{t('text611')}</p>
              {phone ? <MenuContact contact={phone} icon="phone-call" /> : null}
              {email ? (
                <MenuContact
                  contact={email}
                  icon="at"
                  iconClassName="text-[#D7191F] [&_[opacity]]:opacity-20"
                />
              ) : null}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MenuContact({
  contact,
  icon,
  iconClassName,
}: {
  contact: ContactLink;
  icon: IconName;
  iconClassName?: string;
}) {
  return (
    <a
      href={contact.href}
      aria-label={contact.label ? `${contact.label} ${contact.value}` : undefined}
      className={cn(
        'dlz-body-s-medium flex w-fit items-center gap-1 rounded-sm text-[#0F1012] transition-colors hover:text-[#D7191F]',
        FOCUS,
      )}
    >
      <Icon name={icon} size={20} className={iconClassName} />
      {contact.value}
    </a>
  );
}
